import { type ReactNode, useEffect } from "react";
import { RouterProvider } from "@/providers/router-provider";

// Block link navigation inside the preview while leaving the listing's own controls
// (carousel arrows, Summary/Reviews tabs — buttons, not links) fully interactive.
// Native `<a>` clicks (e.g. links inside a rich-text description) are cancelled in the
// capture phase; react-aria `Link`s (creator link, CTAs) navigate through RouterProvider,
// which we point at a no-op below.
function useBlockNavigation() {
  useEffect(() => {
    const blockAnchors = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest("a")) event.preventDefault();
    };
    document.addEventListener("click", blockAnchors, true);
    return () => document.removeEventListener("click", blockAnchors, true);
  }, []);
}

const noNavigate = () => {};

interface PreviewLayoutProps {
  children: ReactNode;
}

/**
 * Minimal layout for the owner-scoped listing preview rendered inside the publish-stage
 * iframe. Deliberately omits everything `PersistentLayout` wires up that would make the
 * embedded document act like a real session: PostHog (init / identify / `$pageview`),
 * Intercom, push notifications, and flash toasts. The preview must be silent and
 * side-effect-free — no analytics for the owner's own self-views, no chat widget, no
 * websockets. `RouterProvider` is kept (so react-aria `Link`s render) but pointed at a
 * no-op navigate, so the preview can't navigate away while its content stays interactive.
 */
export default function PreviewLayout({ children }: PreviewLayoutProps) {
  useBlockNavigation();
  return <RouterProvider navigate={noNavigate}>{children}</RouterProvider>;
}
